import cx from 'classnames'
import Styles from './YoutubePostDetails.module.scss'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ScheduledPostTarget } from 'types'
import { useState } from 'react'
import { YoutubeVisibilityLevel, YOUTUBE_CATEGORIES } from './constants'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { useUpdateScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useUpdateScheduledPostTarget'
import { YoutubePostSettings } from 'api/clients/ScheduledPostsClient'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { AiClient } from 'api/clients/AiClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Select } from 'components/Select/Select'
import { Item } from '@react-stately/collections'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { AIFieldWithAutogen } from './AIFieldWithAutogen'
import { YoutubeCategorySection } from './YoutubeCategorySection'
import {
  AIGEN_FIELDS,
  YOUTUBE_VISIBILITY_LEVELS,
  YOUTUBE_VISIBILITY_LABELS,
  YOUTUBE_VISIBILITY_ICONS,
  YOUTUBE_DISABLED_KEYS,
} from './constants'
import { getDisabledKeyTooltip } from './YoutubeUtils'
import { buildErrorMessage } from 'utils'

interface YoutubePostDetailsProps {
  fileId?: string
  channel: DesignChannel
  scheduledPost: ScheduledPostShow
  scheduledPostTarget?: ScheduledPostTarget
  brandKitId?: number
  isModal?: boolean
  compact?: boolean
}

export const YoutubePostDetails = ({
  scheduledPost,
  scheduledPostTarget,
  brandKitId,
  isModal,
  compact,
}: YoutubePostDetailsProps) => {
  const updateScheduledPost = useUpdateScheduledPost({ suppressToast: true })
  const updateScheduledPostTarget = useUpdateScheduledPostTarget()
  const { data } = useBrandKit(brandKitId)
  const brandKit = data?.brand_kit
  const brandVoiceId = brandKit?.brand_voice?.id

  const isTargetMode = !!scheduledPostTarget

  const defaultSettings: YoutubePostSettings = {
    title: scheduledPost.source_title || scheduledPost.almanac_file?.title || '',
    description: scheduledPost.content || scheduledPost.post_data?.description || '',
    privacy_level: YoutubeVisibilityLevel.Public,
    tags: [],
    category: { id: '', etag: '', title: '' },
    age_level: 'no',
  }

  const stored = (isTargetMode ? scheduledPostTarget.target_overrides?.post_settings : scheduledPost.post_settings) as
    | YoutubePostSettings
    | undefined

  const postSettings: YoutubePostSettings = { ...defaultSettings, ...stored }

  const [contentValues, setContentValues] = useState({
    title: postSettings.title ?? '',
    description: postSettings.description ?? '',
  })

  const updateSettings = (updatedSettings: YoutubePostSettings) => {
    if (isTargetMode) {
      updateScheduledPostTarget.mutate({
        scheduled_post_id: scheduledPostTarget.scheduled_post_id,
        target_id: scheduledPostTarget.id,
        target_overrides: {
          ...scheduledPostTarget.target_overrides,
          post_settings: updatedSettings,
        },
      })
    } else {
      updateScheduledPost.mutate({
        id: scheduledPost.id,
        params: { postSettings: updatedSettings },
      })
    }
  }

  const handleUpdateCategory = (key: React.Key) => {
    const selected = YOUTUBE_CATEGORIES.items.find((category) => category.id === String(key))
    if (!scheduledPost?.id || !selected) return

    const newCategory = {
      id: selected.id,
      etag: selected.etag,
      title: selected.snippet.title,
    }

    updateSettings({ ...postSettings, category: newCategory })
  }

  const handleUpdatePrivacyLevel = (privacyLevel: YoutubeVisibilityLevel) => {
    if (!scheduledPost?.id) return
    updateSettings({ ...postSettings, privacy_level: privacyLevel })
  }

  const handleGenerate = async (key: 'title' | 'description') => {
    const response = await AiClient.captionGeneration({
      prompt: contentValues[key as 'title' | 'description'],
      brandVoiceId: brandVoiceId,
      language: 'english',
    })

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error(
        buildErrorMessage(response.data.errors) ||
          'An error occurred! Our team has been notified and we are looking into it.',
      )
    }
    return response.data
  }

  return (
    <div
      className={cx(Styles.sectionsContainer, { [Styles.modal]: isModal, [Styles.compact]: compact })}
      data-testid="youtube-post-details"
    >
      <div className={Styles.metadataFields}>
        {AIGEN_FIELDS.map((field) => (
          <AIFieldWithAutogen
            key={field.fieldToGenerate}
            fieldToGenerate={field.fieldToGenerate as 'title' | 'description'}
            label={field.label}
            isTextarea={field.isTextarea}
            value={contentValues[field.fieldToGenerate as keyof typeof contentValues]}
            setContentValues={setContentValues}
            handleGenerate={handleGenerate}
            contentValues={contentValues}
            currentPostSettings={postSettings}
            updateSettings={updateSettings}
          />
        ))}
        <div className={Styles.visibilityField}>
          <BaseField label="Visibility" />
          <Select
            variant="secondary"
            selectedKey={postSettings.privacy_level}
            onSelectionChange={(key) => handleUpdatePrivacyLevel(key as YoutubeVisibilityLevel)}
            useFullWidth
            useButtonWidth
            aria-label="Visibility selection"
            placeholder="Select who can view your post"
            disabledKeys={YOUTUBE_DISABLED_KEYS}
            data-testid="visibility-select"
          >
            {YOUTUBE_VISIBILITY_LEVELS.map((level) => {
              const Icon = YOUTUBE_VISIBILITY_ICONS[level]

              return (
                <Item key={level} textValue={level}>
                  <Tooltip hide={!YOUTUBE_DISABLED_KEYS.includes(level)} label={getDisabledKeyTooltip(level)}>
                    <div className={Styles.privacyItem}>
                      <Icon />
                      {YOUTUBE_VISIBILITY_LABELS[level]}
                    </div>
                  </Tooltip>
                </Item>
              )
            })}
          </Select>
        </div>
        <YoutubeCategorySection category={postSettings.category} handleUpdateCategory={handleUpdateCategory} />
      </div>
    </div>
  )
}
